import type { AnyBlockField } from '../api';

/**
 * 收起状态的区块，用一句"这段自己写的文案"说明它是什么。
 *
 * 为什么不是"取 props 里第一个字符串"：props 里有按钮链接（`/contact`）、
 * 图片地址、`start/medium` 这类枚举，随便取一个会比不显示更让人糊涂。
 * 区块库的 `fields` 描述符恰好带着"哪几个字段是给客户写字用的"和表单顺序，
 * 所以按字段走：第一个 `text/textarea` 就是客户在这一段里写的标题。
 *
 * 只读，不做任何改动 —— 摘要是**视图**，写回 props 的想法一步都不要有。
 */

/** 看起来是地址/锚点的值不是文案。`mailto:` 在但 `mailto:x@y` 也不该当摘要 */
const LOOKS_LIKE_LINK = /^(?:[a-z][a-z0-9+.-]*:|\/|#)/i;

/**
 * 单个字段最多取这么多字符。
 *
 * 评价的 quote、FAQ 的 answer 可以有两千字，全塞进 DOM 里会让整页的
 * `textContent` 变得不可搜索（客户在浏览器里 Ctrl+F 会命中收起的那一段，
 * 却看不到它在哪）。真正显示时 CSS 还会再截一次。
 */
const VALUE_CAP = 60;

/** 最多拼两段：标题 + 一句副标题。第三段开始就没人看了 */
const MAX_PARTS = 2;

/**
 * 富文本字段（`rich-text` 的正文）存的是白名单 HTML，摘要里不能把标签带出来。
 *
 * 只在这串东西确实长得像 HTML 时才动手：标题里出现 `Tolerance <0.01mm`
 * 是正常的，一律 `<[^>]*>` 清洗会把它后半截吃掉。认标签名就不会误伤。
 */
const HTML_TAG = /<\/?(?:p|br|strong|em|b|i|a|ul|ol|li|h[1-6]|blockquote)\b/i;

function plainText(value: string): string {
  const text = HTML_TAG.test(value) ? value.replace(/<[^>]*>/g, ' ') : value;
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * @param props 区块当前的 props（草稿态，不是已保存的那份）
 * @param fields 区块库给的表单描述；缺失时返回空串 —— 宁可不显示，也别瞎猜
 */
export function blockSummary(props: Record<string, unknown>, fields?: AnyBlockField[]): string {
  if (!fields?.length) return '';

  const parts: string[] = [];
  const counts: string[] = [];

  for (const field of fields) {
    if (field.type === 'text' || field.type === 'textarea') {
      if (parts.length >= MAX_PARTS) continue;
      const value = props[field.key];
      if (typeof value !== 'string') continue;
      // 换行/连续空格折成一个空格：摘要是一行字，textarea 里的排版在这里没有意义
      const text = plainText(value);
      if (!text || LOOKS_LIKE_LINK.test(text)) continue;
      // 标题和副标题写成同一句时只显示一次。客户复制粘贴的不少见
      if (parts.includes(text)) continue;
      parts.push(text.slice(0, VALUE_CAP));
      continue;
    }

    if (field.type === 'list' || field.type === 'stringList') {
      const value = props[field.key];
      if (!Array.isArray(value) || value.length === 0) continue;
      /*
       * 条目数是这一段唯一可说的信息：FAQ 有 6 条、客户墙有 12 个 Logo。
       * `itemLabel` 本身就带量词（"个问题"、"条评价"），直接拼就行；
       * stringList（如分类 slug）没有它，退回字段名。
       */
      counts.push(
        field.type === 'list'
          ? `${value.length} ${field.itemLabel}`
          : `${value.length} 项${field.label}`,
      );
    }
  }

  return [...parts, ...counts].join(' · ');
}
